import type { GlobalRole } from '@opsflow/api-contracts';
export interface BusinessContext {
  businessDate: string;
  timeZone: string;
  serverNow: string;
  nextBusinessDateAt: string;
}

type LifecycleStatus = 'DRAFT' | 'ACTIVE' | 'RETIRED';
type ResourceType = 'PROJECT_ASSIGNMENT';
export type ChangeAction =
  'CREATED' | 'REACTIVATED' | 'DATES_CORRECTED' | 'SUCCEEDED' | 'ENDED' | 'CANCELED';
export type ReferenceErrorCode =
  'NOT_FOUND' | 'UNAUTHENTICATED' | 'FORBIDDEN' | 'UNAVAILABLE' | 'VALIDATION' | 'STALE' | 'USED';

interface ReferenceHead {
  id: string;
  name: string;
  version: number;
  revisionId: string;
  status: LifecycleStatus;
  firstUsed: boolean;
  effectiveOn: string;
}

type Client = ReferenceHead;

interface Project extends ReferenceHead {
  clientId: string;
}

export interface Person {
  id: string;
  displayName: string;
  role?: GlobalRole;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface ProjectAssignment {
  id: string;
  version: number;
  revisionId?: string | undefined;
  personId: string;
  projectId: string;
  validFrom: string;
  validTo: string | null;
  state: 'SCHEDULED' | 'ACTIVE' | 'ENDED' | 'RETIRED';
  readonly canCorrectDates: boolean;
  readonly canBackdateStart: boolean;
  supersedesId: string | null;
}

export interface ReferenceChange {
  id: string;
  resourceType: ResourceType;
  resourceId: string;
  revisionId?: string | undefined;
  relatedPersonId: string | null;
  action: ChangeAction;
  effectiveOn: string;
  recordedAt: string;
  actorPersonId: string;
  actorDisplayName: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  requestId: string;
}

export interface ReferenceFilter {
  query?: string;
  status?: LifecycleStatus | 'ALL';
  businessDate?: string;
}

interface Governance {
  effectiveOn: string;
}

interface VersionCheck {
  expectedVersion: number;
}

type CreateClient = Pick<Client, 'name'>;
type UpdateClient = Pick<Client, 'name'> & Governance & VersionCheck;
type CreateProject = Pick<Project, 'clientId' | 'name'>;
type UpdateProject = Pick<Project, 'name'> & Governance & VersionCheck;
type AssignmentDates = Pick<ProjectAssignment, 'validFrom' | 'validTo'>;
type CreateAssignment = AssignmentDates & Pick<ProjectAssignment, 'personId' | 'projectId'>;
type CreateAssignments = AssignmentDates &
  ({ personId: string; personIds?: never } | { personId?: never; personIds: string[] }) & {
    projectIds: string[];
  };
type CorrectAssignment = AssignmentDates & VersionCheck;
type DatedAssignmentChange = Governance & VersionCheck & { validTo: string | null };

export interface HistoryFilter {
  resourceType?: ResourceType;
  resourceId?: string;
  personId?: string;
  action?: ChangeAction;
  effectiveFrom?: string;
  effectiveTo?: string;
  cursor?: string;
  limit?: number;
}

export class ReferenceDataError extends Error {
  constructor(
    public readonly code: ReferenceErrorCode,
    message: string,
    public readonly fieldErrors: Record<string, string> = {},
    /** The server's structured `details`, kept so a refusal can explain itself. */
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ReferenceDataError';
  }
}

export function isTransientReferenceError(error: unknown) {
  return error instanceof ReferenceDataError && error.code === 'UNAVAILABLE';
}

export interface AdminReferenceDataSource {
  getBusinessContext(signal?: AbortSignal): Promise<BusinessContext>;
  listClients(filter?: ReferenceFilter, signal?: AbortSignal): Promise<Client[]>;
  getClient(id: string, businessDate?: string, signal?: AbortSignal): Promise<Client>;
  createClient(input: CreateClient): Promise<Client>;
  updateClient(id: string, input: UpdateClient): Promise<Client>;
  listProjects(
    clientId: string,
    filter?: ReferenceFilter,
    signal?: AbortSignal,
  ): Promise<Project[]>;
  getProject(id: string, businessDate?: string, signal?: AbortSignal): Promise<Project>;
  createProject(input: CreateProject): Promise<Project>;
  updateProject(id: string, input: UpdateProject): Promise<Project>;
  listPeople(query?: string, signal?: AbortSignal): Promise<Person[]>;
  getPerson(
    id: string,
    businessDate?: string,
    signal?: AbortSignal,
  ): Promise<Person & { assignmentCounts: { active: number; scheduled: number } }>;
  listAssignments(
    filter: { personId?: string; projectId?: string; businessDate?: string },
    signal?: AbortSignal,
  ): Promise<ProjectAssignment[]>;
  getAssignment(
    id: string,
    businessDate?: string,
    signal?: AbortSignal,
  ): Promise<ProjectAssignment>;
  createAssignment(input: CreateAssignment): Promise<ProjectAssignment>;
  createAssignments(input: CreateAssignments): Promise<ProjectAssignment[]>;
  correctAssignment(id: string, input: CorrectAssignment): Promise<ProjectAssignment>;
  succeedAssignment(id: string, input: DatedAssignmentChange): Promise<ProjectAssignment>;
  endAssignment(id: string, input: Governance & VersionCheck): Promise<ProjectAssignment>;
  removeAssignment(id: string, input: VersionCheck): Promise<ProjectAssignment>;
  reactivateAssignment(id: string, input: DatedAssignmentChange): Promise<ProjectAssignment>;
  listHistory(
    filter?: HistoryFilter,
    signal?: AbortSignal,
  ): Promise<{ items: ReferenceChange[]; nextCursor: string | null }>;
  getChange(id: string, signal?: AbortSignal): Promise<ReferenceChange>;
  reset(): Promise<void>;
}
